// Resolución del ULID de Operations → id de la ficha CANÓNICA del CRM.
//
// El CRM proyecta los leads del monorepo con el MISMO ULID como PK, pero con
// una excepción (regla firme #3 del CRM, "el email es identidad — 1ª ULID
// gana"): si un lead se RE-REGISTRA en el monorepo (ULID nuevo, mismo email),
// el CRM descarta el ULID nuevo y mantiene la ficha bajo el ULID antiguo,
// dejando el nuevo como alias-tombstone (`lead_merged_into`) — igual que un
// lead absorbido por fusión. Con el ULID de la ruta de Operations tal cual, los
// paneles CRM salían vacíos y el link daba 404 aunque la ficha del CRM tuviera
// tickets (incidente isisdeba@gmail.com, 2026-07-22).
//
// Estrategia:
//   1. GET /leads/resolve?monorepoUlid=… — desde 2026-07-22.c (DECISIONS del
//      CRM) sigue la cadena de tombstones: `leadId` de la respuesta ES la ficha
//      canónica (puede diferir del ULID pedido). 200 → listo.
//   2. Si 404 (lead nunca proyectado — p.ej. evento perdido — pero con ficha en
//      el CRM bajo otro id) y tenemos el email del lead, GET /search?q=<email>:
//      el email es UNIQUE en la projection del CRM, así que un match EXACTO por
//      email es la ficha canónica de esta persona.
//   3. Sin match o error no-401 → fallback al ULID de Operations: mismo
//      comportamiento que sin resolución (paneles vacíos), nunca peor.
//
// Un 401 se propaga tal cual: useCrmResource lo silencia y CrmConnectGate
// gestiona la reconexión, igual que en el resto de paneles CRM.

import { CrmHttpError, crmRequest } from "./crm";
import { canonicalLeadId } from "./crmLeadId";
import { searchPersonLeads } from "./crmPerson";

export type CrmLeadResolution = {
  /** Id de ficha del CRM a usar en paneles y links (Crockford-26). */
  crmLeadId: string;
  /**
   * Cómo se resolvió: 'ulid' = vía /leads/resolve (caso normal; si el ULID era
   * un alias/absorbido el CRM ya devuelve la ficha superviviente); 'email' =
   * ficha canónica encontrada por email (lead sin proyectar bajo ese ULID);
   * 'fallback' = no se pudo resolver, se usa el ULID de Operations tal cual
   * (comportamiento previo). Para saber si el id difiere del de la ruta,
   * compara `crmLeadId` con el leadUlid pedido — pasa tanto con 'ulid'
   * (tombstone) como con 'email'.
   */
  via: "ulid" | "email" | "fallback";
};

export async function resolveCrmLead(
  leadUlid: string,
  email: string | null,
): Promise<CrmLeadResolution> {
  try {
    const res = await crmRequest<{ leadId: string }>(
      `/leads/resolve?monorepoUlid=${encodeURIComponent(leadUlid)}`,
    );
    return { crmLeadId: canonicalLeadId(res.leadId), via: "ulid" };
  } catch (err) {
    if (err instanceof CrmHttpError && err.status === 401) throw err;
    if (!(err instanceof CrmHttpError && err.status === 404)) {
      // CRM caído / projection DB deshabilitada / red: no degradar más que hoy.
      return { crmLeadId: leadUlid, via: "fallback" };
    }
  }

  const needle = email?.trim().toLowerCase();
  if (!needle) return { crmLeadId: leadUlid, via: "fallback" };

  try {
    const results = await searchPersonLeads(needle, 5);
    // Match EXACTO de email (la búsqueda es libre y podría devolver parecidos).
    const hit = results.find((r) => r.email?.trim().toLowerCase() === needle);
    if (hit) return { crmLeadId: hit.id, via: "email" };
  } catch (err) {
    if (err instanceof CrmHttpError && err.status === 401) throw err;
    // Búsqueda caída: fallback silencioso (los paneles ya avisan por su cuenta).
  }
  return { crmLeadId: leadUlid, via: "fallback" };
}
